import React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { colors } from '../../theme/tokens';
import { fontFamilies } from '../../theme/fonts';
import { CARD_SHADOW, HEADER_RADIUS } from '../../theme/radius';
import { gradientText } from '../../theme/gradientText';
import { IState } from '../../data/types';
import { isStudent, isTrainer } from '../../data/appRoles';
import HeaderActions from './HeaderActions';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    minHeight: 72,
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 3.5),
    background: colors.bgSurface,
    border: `1px solid ${colors.border}`,
    borderRadius: HEADER_RADIUS,
    boxShadow: CARD_SHADOW,
  },
  text: { minWidth: 0 },
  title: {
    margin: 0,
    fontFamily: fontFamilies.display,
    fontSize: 'clamp(1.1rem, 0.95rem + 0.5vw, 1.4rem)',
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    ...gradientText,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  /** Pages without a title: just the controls, no empty card. */
  slim: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  extra: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) },
  subtitle: {
    margin: theme.spacing(0.25, 0, 0),
    fontSize: 15,
    color: colors.textSecondary,
  },
}));

interface IProps {
  /** Page title (staff screens). */
  title?: string;
  /** One-line description under the title. */
  subtitle?: string;
  /** Page-level controls (help, filters) shown beside the profile controls. */
  extra?: React.ReactNode;
}

/**
 * Desktop page header: a rounded card with the page title on the left and the
 * theme switch, notifications and profile on the right. Below `md` the app
 * bar is shown instead.
 */
const PageHeader = ({ title, subtitle: pageSubtitle, extra }: IProps) => {
  const classes = useStyles();
  const user = useSelector((state: IState) => state.core.user);

  // Screens without a title of their own (the home dashboards) greet the
  // student or instructor by name.
  let heading = title;
  let subtitle: string | undefined = pageSubtitle;
  if (title) {
    // the page names itself
  } else if (isStudent(user)) {
    heading = user?.fullName;
    subtitle = 'Student Portal';
  } else if (isTrainer(user)) {
    heading = user?.fullName;
    subtitle = `${user?.contactId ? `ID: ${user.contactId} · ` : ''}Instructor`;
  }

  if (!heading) {
    return (
      <div className={classes.slim}>
        {extra}
        <HeaderActions />
      </div>
    );
  }

  return (
    <header className={classes.card}>
      <div className={classes.text}>
        {heading && <h1 className={classes.title}>{heading}</h1>}
        {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
      </div>
      <div className={classes.extra}>
        {extra}
        <HeaderActions />
      </div>
    </header>
  );
};

export default PageHeader;
